"use client";

import * as React from "react";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { CustomerStory } from "@/lib/api";

const AUTO_ADVANCE_MS = 6000;
const MAX_VISIBLE = 10;

function CustomerStoriesSection({ stories: allStories }: { stories: CustomerStory[] }) {
  const [index, setIndex] = React.useState(0);
  const [paused, setPaused] = React.useState(false);

  // Total/average reflect every review ever collected; the slider itself
  // only shows the most recent ones so it never grows unwieldy.
  const totalCount = allStories.length;
  const overallRating = totalCount > 0 ? allStories.reduce((sum, s) => sum + s.rating, 0) / totalCount : 0;
  const stories = React.useMemo(
    () =>
      [...allStories]
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, MAX_VISIBLE)
        .sort((a, b) => a.sort_order - b.sort_order),
    [allStories],
  );

  React.useEffect(() => {
    if (paused || stories.length <= 1) return;
    const timer = setInterval(() => setIndex((i) => (i + 1) % stories.length), AUTO_ADVANCE_MS);
    return () => clearInterval(timer);
  }, [paused, stories.length]);

  if (stories.length === 0) return null;

  const story = stories[index];

  return (
    <div>
      <div className="mx-auto max-w-2xl text-center">
        <h2 className="text-2xl font-semibold text-foreground sm:text-3xl">What clinic owners are saying</h2>
        <div className="mt-3 flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <span className="flex items-center gap-0.5 text-warning" aria-hidden="true">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} className={cn("size-4", i < Math.round(overallRating) && "fill-current")} />
            ))}
          </span>
          <span>
            {overallRating.toFixed(1)} out of 5 &middot; {totalCount} review{totalCount === 1 ? "" : "s"}
          </span>
        </div>
      </div>

      <div
        className="relative mx-auto mt-10 max-w-2xl"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <Card>
          <CardContent className="space-y-4 pt-6 text-center">
            <div className="flex justify-center gap-0.5 text-warning" aria-hidden="true">
              {Array.from({ length: story.rating }).map((_, i) => (
                <Star key={i} className="size-4 fill-current" />
              ))}
            </div>
            <p className="text-base text-foreground">&ldquo;{story.quote}&rdquo;</p>
            <div>
              <p className="text-sm font-semibold text-foreground">{story.author_name}</p>
              {story.author_role && <p className="text-xs text-muted-foreground">{story.author_role}</p>}
            </div>
          </CardContent>
        </Card>

        {stories.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous story"
              onClick={() => setIndex((i) => (i - 1 + stories.length) % stories.length)}
              className="absolute left-0 top-1/2 flex size-9 -translate-x-4 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm transition-colors hover:text-foreground sm:-translate-x-12"
            >
              <ChevronLeft className="size-4" />
            </button>
            <button
              type="button"
              aria-label="Next story"
              onClick={() => setIndex((i) => (i + 1) % stories.length)}
              className="absolute right-0 top-1/2 flex size-9 translate-x-4 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm transition-colors hover:text-foreground sm:translate-x-12"
            >
              <ChevronRight className="size-4" />
            </button>

            <div className="mt-6 flex justify-center gap-2">
              {stories.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  aria-label={`Go to story ${i + 1}`}
                  onClick={() => setIndex(i)}
                  className={cn("size-2 cursor-pointer rounded-full transition-colors", i === index ? "bg-primary" : "bg-border hover:bg-muted-foreground")}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export { CustomerStoriesSection };
